import '@assets/styles/components/lms/_common-modal.scss';
import { ReactElement, useState } from 'react';
import { Dropdown, TextInput } from '@components/common';
import { useFormik } from 'formik';
import {
	IContractorDetailsPayload,
	IContractorDetailsResponse,
	useContractorDetailsMutation,
} from '@services/hooks/lms/addNewSite';
import { typeOfContractor } from '@config/constant';
import { useAddNewSiteStore } from '@store/lms/addNewSiteStore';
import { ToastType, useTosterStore } from '@store/toster';
import { IContractorDetailsProps } from '.';
import { keyDownEvent } from '@helper/utils';
import { useValidation } from '@hooks/validations';
import { ConfirmationModal } from '@components/conductedMeetings/confirmationModal/confirmationModal';
import { Modal } from '@components/common/modal/modal';

export const ContractorDetailsModal = ({
	isContractorExists,
	name,
	phone,
	contractorDetails,
	onCancel,
}: IContractorDetailsProps): ReactElement => {
	const { setToaster } = useTosterStore();
	const { setKeyBaseValue } = useAddNewSiteStore();

	const { isPending: isContractorDetailsPending, mutate: contractorDetailsMutation } = useContractorDetailsMutation();
	const { LmsContractorDetailsValidationSchema } = useValidation();

	const [showConfirmation, setShowConfirmation] = useState(false);
	const [confirmationMessage, setConfirmationMessage] = useState('');

	const formik = useFormik({
		initialValues: {
			typeOfContracts: '',
			noOfSites: '',
			contractorPotentialPerMonth: '',
		},
		validationSchema: LmsContractorDetailsValidationSchema,
		enableReinitialize: true,
		onSubmit: async () => {
			try {
				if (isContractorExists) {
					await handleConfirmationDetails();
				} else {
					await addNewContractorDetails();
				}
			} catch (error) {
				setToaster(true, ToastType.error, 'Failed to update contractor details');
			}
		},
	});

	const updateContractorDetails = async (): Promise<void> => {
		const contractName =
			typeOfContractor.find((contractor) => contractor.value === formik.values.typeOfContracts)?.name ?? '';
		await Promise.all([
			setKeyBaseValue('typeOfContracts', contractName),
			setKeyBaseValue('noOfSites', formik.values.noOfSites),
			setKeyBaseValue('contractorPotentialPerMonth', formik.values.contractorPotentialPerMonth),
		]);
		formik.resetForm();
		setShowConfirmation(false);
		onCancel();
	};

	const addNewContractorDetails = async (): Promise<void> => {
		const payload: IContractorDetailsPayload = {
			name: name ?? '',
			phone: phone ?? '',
		};

		contractorDetailsMutation(payload, {
			onSuccess: async (data: IContractorDetailsResponse) => {
				await Promise.all([
					setKeyBaseValue('appIdC', data.app_id_c),
					setKeyBaseValue('membershipNo', data.membershipno ?? ''),
				]);
				await updateContractorDetails();
			},
			onError: (error: Error) => {
				setToaster(true, ToastType.error, error.message);
			},
		});
	};

	const handleConfirmationDetails = async (): Promise<void> => {
		const { noOfSites, contractorPotentialPerMonth } = formik.values;
		const areAllFieldsEmpty =
			contractorDetails?.type_of_contracts == null &&
			contractorDetails?.no_of_sites == null &&
			contractorDetails?.contractor_potential_per_month == null;

		if (areAllFieldsEmpty) {
			await updateContractorDetails();
			return;
		}

		const newNoOfSites = Number(noOfSites);
		const newContractorPotential = Number(contractorPotentialPerMonth);
		const currentNoOfSites = Number(contractorDetails.no_of_sites);
		const currentContractorPotential = Number(contractorDetails.contractor_potential_per_month);

		const isNoOfSitesChanged = currentNoOfSites !== newNoOfSites;
		const isContractorPotentialChanged = currentContractorPotential !== newContractorPotential;

		if (isNoOfSitesChanged || isContractorPotentialChanged) {
			let message = '';

			if (isNoOfSitesChanged && isContractorPotentialChanged) {
				message = `Current number of sites is ${currentNoOfSites} and contractor potential per month is ${currentContractorPotential}. Are you sure you want to change?`;
			} else if (isNoOfSitesChanged) {
				message = `Current number of sites is ${currentNoOfSites}. Are you sure you want to change it to ${newNoOfSites}?`;
			} else if (isContractorPotentialChanged) {
				message = `Current contractor potential per month is ${currentContractorPotential}. Are you sure you want to change it to ${newContractorPotential}?`;
			}

			setConfirmationMessage(message);
			setShowConfirmation(true);
		} else {
			updateContractorDetails();
		}
	};
	// Safe access to contractor details with comprehensive fallbacks
	const getContractorDisplayName = (): string => {
		return name ?? '';
	};

	const getBdeName = (): string => {
		return contractorDetails?.bde_name ?? '';
	};

	const getClubName = (): string => {
		return contractorDetails?.club_name ?? '';
	};

	// Check if contractor details are available and valid
	const hasValidContractorDetails = (): boolean => {
		return Boolean(contractorDetails && typeof contractorDetails === 'object');
	};

	const getEnrollmentMessage = (): string => {
		if (isContractorExists && hasValidContractorDetails()) {
			const bdeName = getBdeName();
			const clubName = getClubName();
			if (bdeName && clubName) {
				return `Contractor Enrolled under ${bdeName} in ${clubName}`;
			} else if (bdeName) {
				return `Lead already submitted for enrollment`;
			} else if (clubName) {
				return `Lead already submitted for enrollment`;
			} else {
				return 'Contractor not Enrolled';
			}
		}
		return 'Contractor not Enrolled';
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string): void => {
		let val = e.target.value.replace(/\D/g, '');
		val = val.slice(0, 6);
		formik.setFieldValue(fieldName, val);
	};

	const getSubmitButtonLabel = (): string => {
		if (isContractorDetailsPending) {
			return 'Processing...';
		}
		return isContractorExists ? 'Update Details' : 'Add Contractor';
	};

	return (
		<div className="lms-modal-wrapper">
			<div
				className={`contractor-status-header ${isContractorExists ? 'contractor-enrolled' : 'contractor-not-enrolled'}`}
			>
				<span className={`status-icon ${isContractorExists ? 'icon-thumbs-up' : 'icon-alert-triangle'}`}></span>
				<div className="status-content">{<p className="status-description">{getEnrollmentMessage()}</p>}</div>
			</div>

			<form onSubmit={formik.handleSubmit}>
				<div className="row">
					<div className="form-control d-col d-col-12">
						<TextInput label="Name" id="name" name="name" value={getContractorDisplayName()} disabled />
					</div>
					<div className="form-control d-col d-col-12">
						<TextInput id="phone" name="phone" label="Phone Number" value={phone ?? ''} disabled />
					</div>
					<div className="form-control d-col d-col-12">
						<Dropdown
							label="Type of Contract"
							id="typeOfContracts"
							name="typeOfContracts"
							title={formik.values.typeOfContracts}
							options={typeOfContractor}
							setFieldValue={formik.setFieldValue}
							onBlur={formik.handleBlur}
							error={
								formik.touched.typeOfContracts && formik.errors.typeOfContracts ? formik.errors.typeOfContracts : null
							}
							required
						/>
					</div>
					<div className="form-control d-col d-col-12">
						<TextInput
							label="No. of active sites"
							id="noOfSites"
							name="noOfSites"
							type="tel"
							value={formik.values.noOfSites}
							setFieldValue={formik.setFieldValue}
							onBlur={formik.handleBlur}
							onKeyDown={keyDownEvent}
							onChange={(e) => handleChange(e, 'noOfSites')}
							error={formik.touched.noOfSites && formik.errors.noOfSites ? formik.errors.noOfSites : null}
							required
						/>
					</div>
					<div className="form-control d-col d-col-12">
						<TextInput
							label="Contractor potential per month"
							id="contractorPotentialPerMonth"
							name="contractorPotentialPerMonth"
							type="tel"
							value={formik.values.contractorPotentialPerMonth}
							setFieldValue={formik.setFieldValue}
							onBlur={formik.handleBlur}
							onKeyDown={keyDownEvent}
							onChange={(e) => handleChange(e, 'contractorPotentialPerMonth')}
							error={
								formik.touched.contractorPotentialPerMonth && formik.errors.contractorPotentialPerMonth
									? formik.errors.contractorPotentialPerMonth
									: null
							}
							required
						/>
					</div>
				</div>
				<div className="btn-wrapper">
					<button type="button" className="btn btn-secondary" onClick={onCancel}>
						Cancel
					</button>
					<button type="submit" className="btn btn-primary" disabled={isContractorDetailsPending || !formik.isValid}>
						{getSubmitButtonLabel()}
					</button>
				</div>
			</form>

			<Modal
				modalId="message"
				className="dialog-sm"
				modalOpen={showConfirmation}
				onModalClose={() => {
					setShowConfirmation(false);
				}}
			>
				<ConfirmationModal
					onClose={() => {
						setShowConfirmation(false);
					}}
					onSubmit={updateContractorDetails}
					message={confirmationMessage}
					buttonLabel={'Ok'}
					hideCancelButton={true}
				/>
			</Modal>
		</div>
	);
};
